import { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../hooks/useToast";
import { api } from "../../services/api";
import styles from "./SupportPage.module.css";
import { resolveLoadErrorMessage } from "../../utils/errorMessages";

const ISSUE_TYPES = [
  { value: "billing", label: "Billing Issue", defaultSubject: "Question about my bill" },
  { value: "medication", label: "Medication Question", defaultSubject: "Question about my medication" },
  { value: "account", label: "Account Problem", defaultSubject: "Issue with my account" },
  { value: "shipping", label: "Shipping Delay", defaultSubject: "My order has not arrived" },
  { value: "prescription", label: "Prescription Status", defaultSubject: "Checking on my prescription status" },
  { value: "technical", label: "Technical Issue", defaultSubject: "Technical problem with the website" },
  { value: "other", label: "Other", defaultSubject: "" }
];

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  waiting: "Awaiting Response",
  resolved: "Resolved",
  closed: "Closed"
};

const STATUS_COLORS = {
  open: "statusOpen",
  in_progress: "statusInProgress",
  waiting: "statusWaiting",
  resolved: "statusResolved",
  closed: "statusClosed"
};

const SupportPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("submit");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");
  
  // My Tickets state
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketsError, setTicketsError] = useState(null);
  const [expandedTicket, setExpandedTicket] = useState(null);

  const [formData, setFormData] = useState({
    issueType: "",
    subject: "",
    message: "",
    orderNumber: "",
    noOrderNumber: false
  });

  const [errors, setErrors] = useState({});

  // Fetch tickets on mount and when tab changes to "myTickets"
  useEffect(() => {
    if (activeTab === "myTickets") {
      fetchTickets();
    }
  }, [activeTab]);

  // Auto-refresh tickets every 30 seconds when viewing My Tickets
  useEffect(() => {
    if (activeTab !== "myTickets") return;
    
    const interval = setInterval(() => {
      fetchTickets(true); // silent refresh
    }, 30000);

    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchTickets = async (silent = false) => {
    if (!silent) setLoadingTickets(true);
    try {
      const response = await api.get("/support/tickets");
      setTickets(response.tickets || []);
      setTicketsError(null);
    } catch (err) {
      // Do NOT let a FAILURE fall through to the empty list: `tickets.length === 0`
      // renders "You have not submitted any support tickets yet." plus a *Submit Your
      // First Ticket* call to action, which tells a member who DOES have tickets that
      // they have none and invites a duplicate. Record the failure so the panel can
      // say so and offer a retry. The render gates this on an EMPTY list, so a failed
      // silent refresh never blows away tickets already on screen.
      setTicketsError(resolveLoadErrorMessage(err, "We could not load your tickets."));
      if (!silent) {
        toast.error("Failed to load your tickets");
      }
    } finally {
      if (!silent) setLoadingTickets(false);
    }
  };

  const handleIssueTypeChange = (e) => {
    const issueType = e.target.value;
    const issueConfig = ISSUE_TYPES.find(t => t.value === issueType);
    setFormData(prev => ({
      ...prev,
      issueType,
      subject: issueConfig?.defaultSubject || prev.subject
    }));
    setErrors(prev => ({ ...prev, issueType: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.issueType) newErrors.issueType = "Please select an issue type";
    if (!formData.subject.trim()) {
      newErrors.subject = "Please enter a subject";
    } else if (formData.subject.length > 100) {
      newErrors.subject = "Subject must be 100 characters or less";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Please describe your issue";
    } else if (formData.message.length < 20) {
      newErrors.message = "Message must be at least 20 characters";
    } else if (formData.message.length > 2000) {
      newErrors.message = "Message must be 2000 characters or less";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const response = await api.post("/support/tickets", {
        issueType: formData.issueType,
        subject: formData.subject.trim(),
        message: formData.message.trim()
      });
      setTicketNumber(response.ticketNumber);
      setSubmitted(true);
      toast.success("Your ticket has been submitted successfully!");
      // Refresh tickets list
      fetchTickets(true);
    } catch (err) {
      toast.error(err.message || "Failed to submit ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLiveAgent = () => {
    if (window.Intercom) {
      window.Intercom("show");
    } else {
      toast.error("Live chat is currently unavailable. Please try again later.");
    }
  };

  const handleNewTicket = () => {
    setSubmitted(false);
    setTicketNumber("");
    setFormData({ issueType: "", subject: "", message: "", orderNumber: "", noOrderNumber: false });
    setErrors({});
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  const toggleTicketExpand = (ticketId) => {
    setExpandedTicket(expandedTicket === ticketId ? null : ticketId);
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Contact Support</h1>
        <p>Need help? Open a support ticket or view your existing tickets. We are here to assist you with any questions about your medications, orders, or account.</p>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${activeTab === "submit" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("submit")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="12" y1="18" x2="12" y2="12"></line>
            <line x1="9" y1="15" x2="15" y2="15"></line>
          </svg>
          Submit Ticket
        </button>
        <button 
          className={`${styles.tab} ${activeTab === "myTickets" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("myTickets")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
          </svg>
          My Tickets
          {tickets.length > 0 && <span className={styles.ticketCount}>{tickets.length}</span>}
        </button>
      </div>

      <div className={styles.container}>
        {activeTab === "submit" ? (
          // Submit Ticket Tab
          <>
            {submitted ? (
              <div className={styles.successCard}>
                <div className={styles.successIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h2>Ticket Submitted!</h2>
                <p className={styles.ticketNum}>Your ticket number is:</p>
                <div className={styles.ticketBadge}>{ticketNumber}</div>
                <p className={styles.successMsg}>We have received your request and will respond within 24 hours. You will receive updates via email at <strong>{user?.email}</strong>.</p>
                <div className={styles.successActions}>
                  <button onClick={handleNewTicket} className={styles.secondaryBtn}>Submit Another Ticket</button>
                  <button onClick={() => setActiveTab("myTickets")} className={styles.primaryBtn}>View My Tickets</button>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.formSection}>
                  <h2 className={styles.sectionTitle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <line x1="10" y1="9" x2="8" y2="9"></line>
                    </svg>
                    Open a Ticket
                  </h2>

                  <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.field}>
                      <label htmlFor="issueType">Issue Type <span className={styles.required}>*</span></label>
                      <select
                        id="issueType"
                        name="issueType"
                        value={formData.issueType}
                        onChange={handleIssueTypeChange}
                        className={errors.issueType ? styles.inputError : ""}
                      >
                        <option value="">Select an issue type...</option>
                        {ISSUE_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                      {errors.issueType && <span className={styles.errorText}>{errors.issueType}</span>}
                    </div>
                    {(formData.issueType === "shipping") && (
                      <div className={styles.field}>
                        <label htmlFor="orderNumber">Order Number</label>
                        <div className={styles.orderNumberGroup}>
                          <input
                            type="text"
                            id="orderNumber"
                            name="orderNumber"
                            placeholder="e.g. ORD-12345"
                            value={formData.orderNumber}
                            onChange={handleChange}
                            disabled={formData.noOrderNumber}
                            className={formData.noOrderNumber ? styles.inputDisabled : ""}
                          />
                          <label className={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={formData.noOrderNumber}
                              onChange={(e) => setFormData(prev => ({ ...prev, noOrderNumber: e.target.checked, orderNumber: e.target.checked ? "" : prev.orderNumber }))}
                            />
                            <span>I don't know my order number</span>
                          </label>
                        </div>
                      </div>
                    )}

                    <div className={styles.field}>
                      <label htmlFor="subject">Subject <span className={styles.required}>*</span></label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        maxLength={100}
                        placeholder="Brief summary of your issue"
                        value={formData.subject}
                        onChange={handleChange}
                        className={errors.subject ? styles.inputError : ""}
                      />
                      {errors.subject && <span className={styles.errorText}>{errors.subject}</span>}
                    </div>

                    <div className={styles.field}>
                      <label htmlFor="message">Message <span className={styles.required}>*</span></label>
                      <textarea
                        id="message"
                        name="message"
                        maxLength={2000}
                        placeholder="Please describe your issue in detail..."
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className={errors.message ? styles.inputError : ""}
                      />
                      {errors.message && <span className={styles.errorText}>{errors.message}</span>}
                    </div>

                    <button
                      type="submit"
                      className={styles.submitBtn}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className={styles.spinner}></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit Ticket"
                      )}
                    </button>
                  </form>
                </div>

                <div className={styles.divider}></div>

                <div className={styles.liveAgentSection}>
                  <h2 className={styles.sectionTitle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    Need Immediate Help?
                  </h2>
                  <p className={styles.liveAgentText}>Chat with a live support agent for real-time assistance.</p>
                  <button onClick={handleLiveAgent} className={styles.liveAgentBtn}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                    </svg>
                    Talk to Live Agent
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          // My Tickets Tab
          <div className={styles.ticketsSection}>
            <h2 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
              My Support Tickets
            </h2>

            {loadingTickets ? (
              <div className={styles.loadingState}>
                <span className={styles.spinner}></span>
                <p>Loading your tickets...</p>
              </div>
            ) : ticketsError && tickets.length === 0 ? (
              <div className={styles.errorState} role="alert">
                <p>{ticketsError}</p>
                <button
                  type="button"
                  className={styles.retryButton}
                  onClick={() => fetchTickets()}
                >
                  Try again
                </button>
              </div>
            ) : tickets.length === 0 ? (
              <div className={styles.emptyState}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                <p>You have not submitted any support tickets yet.</p>
                <button onClick={() => setActiveTab("submit")} className={styles.primaryBtn}>
                  Submit Your First Ticket
                </button>
              </div>
            ) : (
              <div className={styles.ticketsList}>
                {tickets.map(ticket => (
                  <div 
                    key={ticket.id} 
                    className={`${styles.ticketCard} ${expandedTicket === ticket.id ? styles.ticketExpanded : ""}`}
                  >
                    <div 
                      className={styles.ticketHeader}
                      onClick={() => toggleTicketExpand(ticket.id)}
                    >
                      <div className={styles.ticketInfo}>
                        <span className={styles.ticketNumberSmall}>{ticket.ticketNumber}</span>
                        <h3 className={styles.ticketSubject}>{ticket.subject}</h3>
                        <span className={styles.ticketDate}>{formatDate(ticket.createdAt)}</span>
                      </div>
                      <div className={styles.ticketActions}>
                        <span className={`${styles.statusBadge} ${styles[STATUS_COLORS[ticket.status]]}`}>
                          {STATUS_LABELS[ticket.status] || ticket.status}
                        </span>
                        <svg 
                          className={`${styles.expandIcon} ${expandedTicket === ticket.id ? styles.expanded : ""}`}
                          width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>
                    </div>
                    
                    {expandedTicket === ticket.id && (
                      <div className={styles.ticketDetails}>
                        <div className={styles.ticketMessage}>
                          <strong>Your Message:</strong>
                          <p>{ticket.messages?.[0]?.content || ticket.message || "No message content available."}</p>
                        </div>
                        {ticket.category && (
                          <div className={styles.ticketMeta}>
                            <span><strong>Category:</strong> {ticket.category}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPage;
